import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Box, Drawer, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretLeft, faCaretRight } from '@fortawesome/free-solid-svg-icons';
import { faXmark } from '@fortawesome/pro-light-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import DevNav from './Navigation';
import ImpersonationDialog from '../Impersonation';
import ImpersonationBanner from '../../common/ImpersonationBanner';
import CourseDialog from '../CourseSwitch';
import { toggleSideNav } from '../../../store/navigationSlice';
import { DRAWER_WIDTH } from '../../common/constants';
import { useIsNarrowScreen } from '../../common/useIsNarrowScreen';
import BreakpointBanner from '../../common/BreakpointBanner';
import Feedback from '../../common/Feedback';
import { logOut } from '../../../auth';

const openedMixin = (theme) => ({
  width: DRAWER_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  width: '75px',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
});

const StyledDrawer = styled(Drawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    /*
     * A flex item defaults to `min-width: auto`, which refuses to shrink below
     * its content's widest unbreakable piece. Next to the 75px drawer that left
     * this element wider than a phone screen, and the `overflowX` below then cut
     * the right-hand side off instead of showing it.
     */
    minWidth: 0,
    /*
     * 'clip' rather than 'hidden'. Both hide horizontal overflow from the drawer
     * transition, but 'hidden' turns this element into a scroll container, which
     * stops `position: sticky` inside a page from ever resolving against the
     * viewport — the quiz question rail scrolled away instead of pinning.
     */
    overflowX: 'clip',
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
  }),
);

function DevPortal() {
  const dispatch = useDispatch();
  const isMobile = useIsNarrowScreen();
  const isImpersonating = useSelector((state) => state.impersonation.isImpersonating);
  const canImpersonate = useSelector((state) => state.user.user?.canImpersonate === true);
  const courseId = useSelector((state) => state.course.courseId);
  const isOpen = useSelector((state) => state.navigation.isSideNavOpen);
  const showBanner = isImpersonating || !!courseId;
  const [isImpersonationDialogOpen, setIsImpersonationDialogOpen] = useState(false);
  const [isCourseDialogOpen, setIsCourseDialogOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <Box className={'mss__devPortal' + (showBanner ? ' impersonating' : '')}>
      <BreakpointBanner />
      <ImpersonationBanner
        onSwitchUser={canImpersonate ? () => setIsImpersonationDialogOpen(true) : undefined}
        onSwitchCourse={() => setIsCourseDialogOpen(true)}
      />

      {isMobile ? (
        <>
          <IconButton
            className="mss__mobileMenuToggle"
            onClick={() => setIsMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <FontAwesomeIcon icon={faCaretDown} />
          </IconButton>

          <Drawer
            variant="temporary"
            anchor="top"
            open={isMobileMenuOpen}
            onClose={() => setIsMobileMenuOpen(false)}
            className="mss__mobileNavDrawer"
          >
            <IconButton
              className="mss__mobileMenuToggle"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              <FontAwesomeIcon icon={faXmark} />
            </IconButton>
            <DevNav
              isOpen
              isMobile
              onImpersonateClick={() => {
                setIsMobileMenuOpen(false);
                setIsImpersonationDialogOpen(true);
              }}
              onSignOut={logOut}
              onNavigate={() => setIsMobileMenuOpen(false)}
            />
          </Drawer>
        </>
      ) : (
        <>
          <StyledDrawer variant="permanent" open={isOpen} className="mss__devNav">
            <DevNav
              isOpen={isOpen}
              onImpersonateClick={() => setIsImpersonationDialogOpen(true)}
              onSignOut={logOut}
            />
          </StyledDrawer>

          <IconButton
            className={'mss__devNavToggler' + (isOpen ? '' : ' closed')}
            onClick={() => dispatch(toggleSideNav())}
            aria-label={isOpen ? 'Collapse menu' : 'Expand menu'}
          >
            <FontAwesomeIcon icon={isOpen ? faCaretLeft : faCaretRight} size="xs" />
          </IconButton>
        </>
      )}

      <Main
        open={isMobile || isOpen}
        className={`mss__devPortalMain${!isMobile && !isOpen ? ' sidebarClosed' : ''}`}
      >
        <Outlet />
      </Main>

      {canImpersonate && (
        <ImpersonationDialog
          open={isImpersonationDialogOpen}
          onClose={() => setIsImpersonationDialogOpen(false)}
        />
      )}
      <CourseDialog open={isCourseDialogOpen} onClose={() => setIsCourseDialogOpen(false)} />

      {/* Offered from every portal page, as it is from every Canvas one. */}
      <Feedback id="portalFeedback" />
    </Box>
  );
}

export default DevPortal;
